import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, ExternalLink, Loader2, Calendar, BellRing, Mail, Plus, X, StickyNote, Heart } from "lucide-react";
import CompanyLogo from "@/components/CompanyLogo";

const STAGES = [
  { key: "saved", label: "Saved", color: "bg-white" },
  { key: "applied", label: "Applied", color: "bg-[#eab308]" },
  { key: "interview", label: "Interview", color: "bg-[#4338ca] text-white" },
  { key: "offer", label: "Offer", color: "bg-[#22c55e] text-white" },
  { key: "rejected", label: "Rejected", color: "bg-[#ec4899] text-white" },
];

function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  const now = new Date();
  const diff = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
  return diff;
}

function fmtWhen(iso) {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function Tracker() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDate, setEditingDate] = useState(null);
  const [editingNotes, setEditingNotes] = useState(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [testingEmail, setTestingEmail] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manual, setManual] = useState({ company: "", job_title: "", job_url: "", status: "applied", notes: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/applications");
      setApps(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const move = async (id, status) => {
    try {
      const { data } = await api.patch(`/applications/${id}`, { status });
      setApps((prev) => prev.map((a) => (a.id === id ? data : a)));
    } catch { toast.error("Update failed"); }
  };
  const setDate = async (id, iso) => {
    try {
      const { data } = await api.patch(`/applications/${id}`, { interview_date: iso });
      setApps((prev) => prev.map((a) => (a.id === id ? data : a)));
      toast.success("Interview date saved — we'll email you a reminder");
    } catch { toast.error("Save failed"); }
  };
  const saveNotes = async (id) => {
    try {
      const { data } = await api.patch(`/applications/${id}`, { notes: notesDraft });
      setApps((prev) => prev.map((a) => (a.id === id ? data : a)));
      setEditingNotes(null); setNotesDraft("");
      toast.success("Notes saved");
    } catch { toast.error("Save failed"); }
  };
  const addManual = async () => {
    if (!manual.company.trim() || !manual.job_title.trim()) {
      toast.error("Company and role are required"); return;
    }
    try {
      const { data } = await api.post("/applications", { ...manual, source: "manual" });
      setApps((prev) => [data, ...prev]);
      setShowManual(false);
      setManual({ company: "", job_title: "", job_url: "", status: "applied", notes: "" });
      toast.success("Application added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/applications/${id}`);
      setApps((prev) => prev.filter((a) => a.id !== id));
      toast.success("Removed");
    } catch { toast.error("Delete failed"); }
  };
  const sendTestReminder = async () => {
    setTestingEmail(true);
    try {
      const { data } = await api.post("/reminders/test");
      toast.success(`Test reminder sent to ${data.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setTestingEmail(false); }
  };

  const byStage = (k) => apps.filter((a) => (a.status || "saved") === k);

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label-eyebrow">Kanban tracker</div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">Your applications</h1>
          <p className="mt-2 text-gray-600">Move cards through stages, add interview dates, and keep private notes. Auto-nudges you after 7 days of no change.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="add-manual-btn" onClick={() => setShowManual((v) => !v)}
            className="nb-btn bg-[#4338ca] text-white px-4 py-2 text-sm inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add manual application
          </button>
          <button data-testid="reminder-test-btn" onClick={sendTestReminder} disabled={testingEmail}
            className="nb-btn bg-white px-4 py-2 text-sm inline-flex items-center gap-2">
            {testingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send test email
          </button>
        </div>
      </div>

      {showManual && (
        <div className="nb-card p-5 mb-6" data-testid="manual-form">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold">Add a job you found elsewhere</h2>
            <button onClick={() => setShowManual(false)} className="nb-pill"><X className="w-3 h-3" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input data-testid="manual-company" value={manual.company} onChange={(e) => setManual((m) => ({ ...m, company: e.target.value }))} placeholder="Company *"
              className="border-2 border-black rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#4338ca]" />
            <input data-testid="manual-title" value={manual.job_title} onChange={(e) => setManual((m) => ({ ...m, job_title: e.target.value }))} placeholder="Job title *"
              className="border-2 border-black rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#4338ca]" />
            <input data-testid="manual-url" value={manual.job_url} onChange={(e) => setManual((m) => ({ ...m, job_url: e.target.value }))} placeholder="Job URL (optional)"
              className="border-2 border-black rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#4338ca]" />
            <select data-testid="manual-status" value={manual.status} onChange={(e) => setManual((m) => ({ ...m, status: e.target.value }))}
              className="border-2 border-black rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#4338ca] bg-white">
              {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <textarea data-testid="manual-notes" value={manual.notes} onChange={(e) => setManual((m) => ({ ...m, notes: e.target.value }))} placeholder="Notes (optional)" rows={3}
              className="md:col-span-2 border-2 border-black rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#4338ca]" />
          </div>
          <div className="mt-3 flex justify-end">
            <button data-testid="manual-save" onClick={addManual} className="nb-btn bg-[#22c55e] text-white px-4 py-2 text-sm">Add to tracker</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>
      ) : (
        <div data-testid="kanban-board" className="flex gap-5 overflow-x-auto pb-6">
          {STAGES.map((s) => (
            <div key={s.key} className="min-w-[300px] w-[300px] shrink-0">
              <div className={`nb-card p-3 mb-3 flex items-center justify-between ${s.color}`}>
                <div className="font-bold uppercase text-sm tracking-wider">{s.label}</div>
                <span className="nb-pill bg-white text-black">{byStage(s.key).length}</span>
              </div>
              <div className="space-y-3">
                {byStage(s.key).map((a) => {
                  const dLeft = daysUntil(a.interview_date);
                  return (
                    <div key={a.id} data-testid={`app-card-${a.id}`} className="nb-card p-4">
                      <div className="flex items-start gap-3">
                        <CompanyLogo company={a.company} size={36} />
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-bold uppercase tracking-widest text-gray-500 truncate">{a.company}</div>
                          <div className="font-bold text-sm leading-snug line-clamp-2 mt-0.5">{a.job_title}</div>
                        </div>
                      </div>

                      {a.notes && editingNotes !== a.id && (
                        <p className="mt-2 text-xs text-gray-600 line-clamp-3 whitespace-pre-wrap">{a.notes}</p>
                      )}
                      {editingNotes === a.id && (
                        <div className="mt-2">
                          <textarea data-testid={`notes-editor-${a.id}`} value={notesDraft}
                            onChange={(e) => setNotesDraft(e.target.value)} rows={4}
                            placeholder="Interview notes — who you met, topics covered, questions asked…"
                            className="w-full border-2 border-black rounded-md px-2 py-1.5 text-xs" />
                          <div className="mt-1 flex gap-1">
                            <button data-testid={`notes-save-${a.id}`} onClick={() => saveNotes(a.id)}
                              className="nb-pill bg-[#22c55e] text-white text-[10px]">save</button>
                            <button onClick={() => { setEditingNotes(null); setNotesDraft(""); }}
                              className="nb-pill text-[10px]">cancel</button>
                          </div>
                        </div>
                      )}
                      {editingNotes !== a.id && (a.status === "interview" || a.status === "offer") && (
                        <button data-testid={`notes-edit-${a.id}`}
                          onClick={() => { setEditingNotes(a.id); setNotesDraft(a.notes || ""); }}
                          className="mt-2 text-[11px] font-bold text-[#4338ca] hover:underline inline-flex items-center gap-1">
                          <StickyNote className="w-3 h-3" /> {a.notes ? "Edit notes" : "Add interview notes"}
                        </button>
                      )}

                      {a.status === "interview" && (
                        <div className="mt-3 border-2 border-dashed border-black/30 rounded-lg p-2">
                          {editingDate === a.id ? (
                            <div className="flex flex-col gap-2">
                              <input data-testid={`interview-date-${a.id}`} type="datetime-local"
                                defaultValue={a.interview_date ? new Date(a.interview_date).toISOString().slice(0, 16) : ""}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val) {
                                    const iso = new Date(val).toISOString();
                                    setDate(a.id, iso);
                                    setEditingDate(null);
                                  }
                                }}
                                className="border-2 border-black rounded-md px-2 py-1 text-xs w-full" />
                              <button onClick={() => setEditingDate(null)} className="nb-pill bg-white text-[10px] self-start">cancel</button>
                            </div>
                          ) : a.interview_date ? (
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-1.5 text-xs">
                                <Calendar className="w-3.5 h-3.5" />
                                <span className="font-bold">{fmtWhen(a.interview_date)}</span>
                                {dLeft !== null && dLeft >= 0 && dLeft <= 3 && (
                                  <span className={`nb-pill text-[10px] ${dLeft <= 1 ? "bg-[#ec4899] text-white" : "bg-[#eab308]"}`}>
                                    <BellRing className="w-3 h-3 mr-0.5" /> {dLeft === 0 ? "today" : dLeft === 1 ? "tomorrow" : `${dLeft}d`}
                                  </span>
                                )}
                              </div>
                              <button data-testid={`edit-date-${a.id}`} onClick={() => setEditingDate(a.id)} className="nb-pill text-[10px]">edit</button>
                            </div>
                          ) : (
                            <button data-testid={`add-date-${a.id}`} onClick={() => setEditingDate(a.id)}
                              className="text-xs font-bold inline-flex items-center gap-1 text-[#4338ca] hover:underline">
                              <Calendar className="w-3.5 h-3.5" /> Add interview date
                            </button>
                          )}
                          {a.reminder_sent_at && <div className="mt-1 text-[10px] text-[#22c55e] font-bold">✉ Reminder sent</div>}
                        </div>
                      )}

                      <div className="mt-3 flex items-center justify-between">
                        <div className="flex flex-wrap gap-1">
                          {STAGES.filter((x) => x.key !== s.key).map((x) => (
                            <button key={x.key} data-testid={`move-${a.id}-${x.key}`} onClick={() => move(a.id, x.key)} title={`Move to ${x.label}`}
                              className="nb-pill text-[10px] hover:bg-black hover:text-white">{x.label[0]}</button>
                          ))}
                        </div>
                        <div className="flex gap-1">
                          {(a.status === "interview" || a.status === "offer") && (
                            <Link to="/thank-you" state={{ application: a }} data-testid={`thankyou-${a.id}`}
                              className="nb-pill bg-[#ec4899] text-white text-[10px]" title="Draft Thank-You letter">
                              <Heart className="w-3 h-3" />
                            </Link>
                          )}
                          {a.job_url && <a href={a.job_url} target="_blank" rel="noopener noreferrer" className="nb-pill" title="Open"><ExternalLink className="w-3 h-3" /></a>}
                          <button data-testid={`delete-${a.id}`} onClick={() => remove(a.id)} className="nb-pill bg-white" title="Delete"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {byStage(s.key).length === 0 && (
                  <div className="text-center text-xs text-gray-500 border-2 border-dashed border-black/30 rounded-lg py-8">No items</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
